import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import ShipmentLabelChip from './ShipmentLabelChip';
import { ShipmentLabelRequest } from '../../api/generated/models/ShipmentLabelRequest';
import type { ShipmentLabelView } from '../../api/generated/models/ShipmentLabelView';

const COLORS = [
  ShipmentLabelRequest.color.PURPLE,
  ShipmentLabelRequest.color.BLUE,
  ShipmentLabelRequest.color.GREEN,
  ShipmentLabelRequest.color.ORANGE,
  ShipmentLabelRequest.color.RED,
  ShipmentLabelRequest.color.GRAY,
] as const;

interface LabelModalProps {
  mode: 'create' | 'edit';
  initial?: ShipmentLabelView | null;
  onClose: () => void;
  onSave: (payload: { name: string; color: ShipmentLabelRequest.color }) => void;
  saving?: boolean;
  errorMessage?: string | null;
}

const LabelModal: React.FC<LabelModalProps> = ({
  mode,
  initial,
  onClose,
  onSave,
  saving = false,
  errorMessage = null,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? '');
  const [color, setColor] = useState<ShipmentLabelRequest.color>(
    (initial?.color as ShipmentLabelRequest.color) ??
      ShipmentLabelRequest.color.PURPLE,
  );

  useEffect(() => {
    setName(initial?.name ?? '');
    setColor(
      (initial?.color as ShipmentLabelRequest.color) ??
        ShipmentLabelRequest.color.PURPLE,
    );
  }, [initial]);

  const title =
    mode === 'create'
      ? t('shipment_label_modal_create_title')
      : t('shipment_label_modal_edit_title');

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      className="shipment-tracker-modal"
    >
      {mode === 'create' && (
        <p className="shipment-tracker-modal__intro">{t('shipment_label_modal_intro')}</p>
      )}
      <label className="form-label">
        {t('shipment_label_field_name')}
        <input
          className="input"
          maxLength={40}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label className="form-label">{t('shipment_label_color_label')}</label>
      <div className="shipment-label-color-picker">
        {COLORS.map((c) => (
          <button
            key={c}
            type="button"
            className={`shipment-label-color-picker__swatch shipment-label-color-picker__swatch--${c}${
              color === c ? ' shipment-label-color-picker__swatch--active' : ''
            }`}
            aria-label={c}
            onClick={() => setColor(c)}
          />
        ))}
      </div>
      <div className="shipment-label-preview-box">
        <span className="shipment-label-preview-box__label">{t('shipment_label_preview')}</span>
        <ShipmentLabelChip
          label={{
            name: name.trim() || t('shipment_label_preview_placeholder'),
            color,
          }}
        />
      </div>
      {errorMessage && <p className="form-error">{errorMessage}</p>}
      <div className="shipment-tracker-modal__footer">
        <button
          type="button"
          className="shipment-tracker-modal__btn shipment-tracker-modal__btn--secondary"
          onClick={onClose}
          disabled={saving}
        >
          {t('cancel')}
        </button>
        <button
          type="button"
          className="shipment-tracker-modal__btn shipment-tracker-modal__btn--primary"
          disabled={saving || !name.trim()}
          onClick={() => onSave({ name: name.trim(), color })}
        >
          {mode === 'create' ? t('shipment_label_create_btn') : t('save')}
        </button>
      </div>
    </Modal>
  );
};

export default LabelModal;
